import { useCallback, useEffect, useRef, useState } from 'react';
import { useForgeStore } from '../state/store.ts';
import { GALLERY, SHEET_GALLERY, TURN_GALLERY, makeSampleFile, SAMPLE_SPECS } from '../core/samples.ts';

/** 파일을 들여오는 세 가지 길: 드래그, 클릭, 붙여넣기. 그리고 샘플. */
export function Dropzone() {
  const loadFile = useForgeStore((s) => s.loadFile);
  const loadSheetFile = useForgeStore((s) => s.loadSheetFile);
  const loadUrl = useForgeStore((s) => s.loadUrl);
  const loadTurnUrls = useForgeStore((s) => s.loadTurnUrls);
  const mode = useForgeStore((s) => s.mode);
  const setMode = useForgeStore((s) => s.setMode);
  const turnaround = useForgeStore((s) => s.turnaround);
  const loadViewFiles = useForgeStore((s) => s.loadViewFiles);
  const fileName = useForgeStore((s) => s.fileName);
  const previewUrl = useForgeStore((s) => s.previewUrl);
  const analysis = useForgeStore((s) => s.analysis);
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const accept = useCallback(
    (files: FileList | null | undefined) => {
      const list = Array.from(files ?? []).filter((f) => f.type.startsWith('image/'));
      const file = list[0];
      if (!file) return;
      // 여러 장이면 턴어라운드 뷰 세트(8장=45°, 4장=90°, 3장=정면·측면·후면).
      if (list.length >= 2) { void loadViewFiles(list); return; }
      void (mode === 'sheet' ? loadSheetFile(file) : loadFile(file));
    },
    [loadFile, loadSheetFile, loadViewFiles, mode],
  );

  // 스크린샷을 찍어서 바로 붙여넣는 게 가장 빠른 경로다.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((i) => i.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (file) void (mode === 'sheet' ? loadSheetFile(file) : loadFile(file));
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [loadFile, loadSheetFile, mode]);

  return (
    <div className="section">
      <h2>입력</h2>
      <div className="chips" style={{ marginBottom: 8 }}>
        <button className="chip" aria-pressed={mode === 'single'} onClick={() => setMode('single')}>
          이미지 한 장
        </button>
        <button className="chip ghost" aria-pressed={mode === 'sheet'} onClick={() => setMode('sheet')}>
          3뷰 캐릭터 시트
        </button>
      </div>
      <div
        className={`dropzone${over ? ' over' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); accept(e.dataTransfer.files); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
      >
        <strong>{mode === 'sheet' ? '시트를 여기에' : '이미지를 여기에'}</strong>
        <span>{mode === 'sheet' ? '3뷰 시트 한 장, 또는 뷰 이미지 여러 장(4·8장) · 드래그 · Ctrl+V' : '드래그 · 클릭 · Ctrl+V'}</span>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => accept(e.target.files)}
        />
      </div>

      {previewUrl && (
        <div className="thumb-row">
          <img className="thumb" src={previewUrl} alt="원본" />
          <div className="filemeta">
            <div className="name">{fileName}</div>
            <div className="sub">
              {analysis
                ? `${analysis.source.width}×${analysis.source.height} · ${maskLabel(analysis.maskSource)} · 피사체 ${Math.round(analysis.coverage * 100)}%`
                : '분석 중'}
            </div>
          </div>
        </div>
      )}

      {mode === 'sheet' ? (
        <div className="gallery sheets">
          {TURN_GALLERY.map((g) => (
            <button
              key={g.id}
              className="gallery-item turn"
              title={`${g.label} — ${g.showcases}`}
              aria-pressed={fileName?.includes(`${g.id}-000`) ?? false}
              onClick={() => { void loadTurnUrls(g.views, g.id); }}
            >
              <span className="turn-strip">
                {g.views.slice(0, 4).map((v) => <img key={v.azimuth} src={v.url} alt={`${v.azimuth}°`} loading="lazy" />)}
              </span>
              <span>{g.label}</span>
            </button>
          ))}
          {SHEET_GALLERY.map((g) => (
            <button
              key={g.id}
              className="gallery-item"
              title={`${g.label} — ${g.showcases}`}
              aria-pressed={fileName === `${g.id}.webp`}
              onClick={() => { void loadUrl(g.url, `${g.id}.webp`, 'sheet'); }}
            >
              <img src={g.url} alt={g.label} loading="lazy" />
              <span>{g.label}</span>
            </button>
          ))}
        </div>
      ) : (
      <div className="gallery">
        {GALLERY.map((g) => (
          <button
            key={g.id}
            className="gallery-item"
            title={`${g.label} — ${g.showcases}`}
            aria-pressed={fileName === `${g.id}.webp`}
            onClick={() => { void loadUrl(g.url, `${g.id}.webp`); }}
          >
            <img src={g.url} alt={g.label} loading="lazy" />
            <span>{g.label}</span>
          </button>
        ))}
      </div>
      )}

      {mode === 'sheet' && turnaround?.split && !turnaround.split.detected && (
        <p className="notice" style={{ marginTop: 10 }}>
          세 뷰 사이의 빈 골짜기를 못 찾아 3등분으로 나눴습니다. 뷰끼리 겹쳐 있거나 넷 이상이면
          결과가 어긋납니다.
        </p>
      )}

      {mode === 'single' && (
      <div className="chips" style={{ marginTop: 8 }}>
        {SAMPLE_SPECS.map((s) => (
          <button
            key={s.id}
            className="chip"
            onClick={() => { void makeSampleFile(s.id).then(loadFile); }}
          >
            절차적: {s.label}
          </button>
        ))}
      </div>
      )}

      {analysis?.maskSource === 'full' && (
        <p className="notice" style={{ marginTop: 10 }}>
          배경을 분리하지 못했습니다. 화면 전체를 피사체로 다룹니다 — <b>릴리프</b> 모드가
          더 나을 수 있습니다. 배경이 단색인 이미지나 알파가 있는 PNG를 넣으면 잘라냅니다.
        </p>
      )}
    </div>
  );
}

function maskLabel(source: 'alpha' | 'flood' | 'full'): string {
  if (source === 'alpha') return '알파 채널 사용';
  if (source === 'flood') return '배경 자동 제거';
  return '배경 분리 실패';
}
